import { useState, useEffect, useCallback, FC } from 'react';
import {
  Users,
  UserPlus,
  UserMinus,
  DollarSign,
  AlertTriangle,
  Zap,
  RefreshCw,
  Plus,
  Trash2,
  Calendar,
  Layers,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Drawer } from '../ui/Drawer';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';
import { WhatsAppGroup } from '../../api/groups';
import {
  fetchHourlyRetention,
  createCampaignAdSpend,
  deleteCampaignAdSpend,
  HourlyRetentionResponse,
  HourlyRetentionBucket,
  CampaignAdSpend,
} from '../../api/monitor';

export interface GroupMonitorViewProps {
  groups: WhatsAppGroup[];
  onNavigateToGroups?: () => void;
  initialDate?: string;
  initialGroupId?: string;
  initialTelemetry?: HourlyRetentionResponse | null;
  initialDrawerOpen?: boolean;
}

export const GroupMonitorView: FC<GroupMonitorViewProps> = ({
  groups,
  onNavigateToGroups,
  initialDate,
  initialGroupId = '',
  initialTelemetry = null,
  initialDrawerOpen = false,
}) => {
  const { token } = useAuth();
  const { success, error } = useToast();

  const getTodayStr = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [selectedDate, setSelectedDate] = useState<string>(
    initialDate || getTodayStr()
  );
  const [selectedGroupId, setSelectedGroupId] =
    useState<string>(initialGroupId);
  const [manualAdSpendInput, setManualAdSpendInput] = useState<string>('');
  const [telemetry, setTelemetry] = useState<HourlyRetentionResponse | null>(
    initialTelemetry
  );
  const [isLoading, setIsLoading] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(initialDrawerOpen);
  const [isSavingAdSpend, setIsSavingAdSpend] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [campaignAmount, setCampaignAmount] = useState('');
  const [campaignDate, setCampaignDate] = useState(selectedDate);
  const [campaignGroupId, setCampaignGroupId] = useState('');
  const [campaignNotes, setCampaignNotes] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Deleting ad spend state
  const [deletingSpendId, setDeletingSpendId] = useState<string | null>(null);

  const loadTelemetry = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const parsedManual = manualAdSpendInput.trim()
        ? parseFloat(manualAdSpendInput.replace(',', '.'))
        : undefined;

      const data = await fetchHourlyRetention(token, {
        date: selectedDate,
        groupId: selectedGroupId || undefined,
        manualAdSpend: isNaN(parsedManual as number) ? undefined : parsedManual,
      });
      setTelemetry(data);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao carregar telemetria de retenção.';
      error(msg, 'Erro');
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedDate, selectedGroupId, manualAdSpendInput, error]);

  useEffect(() => {
    loadTelemetry();
  }, [loadTelemetry]);

  const handleManualSpendChange = (val: string) => {
    setManualAdSpendInput(val);
  };

  const handleOpenDrawer = () => {
    setCampaignName('');
    setCampaignAmount('');
    setCampaignDate(selectedDate);
    setCampaignGroupId(selectedGroupId);
    setCampaignNotes('');
    setFormErrors({});
    setIsDrawerOpen(true);
  };

  const handleSaveAdSpend = async () => {
    if (!token) return;
    const errors: Record<string, string> = {};
    if (!campaignName.trim()) {
      errors.campaignName = 'Nome da campanha é obrigatório.';
    }
    const parsedAmt = parseFloat(campaignAmount.replace(',', '.'));
    if (isNaN(parsedAmt) || parsedAmt < 0) {
      errors.campaignAmount = 'Informe um valor válido e positivo.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSavingAdSpend(true);
    try {
      await createCampaignAdSpend(token, {
        campaign_name: campaignName.trim(),
        amount: parsedAmt,
        date: campaignDate || selectedDate,
        group_id: campaignGroupId || undefined,
        notes: campaignNotes.trim() || undefined,
      });
      success('Investimento em tráfego registrado com sucesso.', 'Gasto Salvo');
      setIsDrawerOpen(false);
      await loadTelemetry();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Erro ao registrar investimento em tráfego.';
      error(msg, 'Erro');
    } finally {
      setIsSavingAdSpend(false);
    }
  };

  const handleDeleteAdSpend = async (spendId: string) => {
    if (!token) return;
    setDeletingSpendId(spendId);
    try {
      await deleteCampaignAdSpend(token, spendId);
      success('Registro de gasto removido com sucesso.', 'Excluído');
      await loadTelemetry();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao excluir registro de gasto.';
      error(msg, 'Erro');
    } finally {
      setDeletingSpendId(null);
    }
  };

  const summary = telemetry?.summary || {
    total_joined: 0,
    total_left: 0,
    net_growth: 0,
    churn_rate: 0,
    total_ad_spend: 0,
    real_cpl: null,
    current_total_members: 0,
  };

  const hourlyData = telemetry?.hourly_data || [];
  const campaigns = telemetry?.campaigns || [];

  // Encontra valor máximo para escala das barras
  const maxActivity = Math.max(
    1,
    ...hourlyData.map((h) => Math.max(h.joined, h.left))
  );

  const activeHours = hourlyData.filter((h) => h.joined > 0 || h.left > 0);

  const hourlyColumns: Column<HourlyRetentionBucket>[] = [
    {
      key: 'hour_label',
      header: 'Horário',
      render: (item) => (
        <span className="font-mono text-xs font-semibold text-content-main">
          {item.hour_label}
        </span>
      ),
    },
    {
      key: 'joined',
      header: 'Entradas (+)',
      render: (item) => (
        <Badge variant={item.joined > 0 ? 'online' : 'neutral'}>
          +{item.joined}
        </Badge>
      ),
    },
    {
      key: 'left',
      header: 'Saídas (-)',
      render: (item) => (
        <Badge variant={item.left > 0 ? 'warning' : 'neutral'}>
          -{item.left}
        </Badge>
      ),
    },
    {
      key: 'net',
      header: 'Saldo Líquido',
      render: (item) => (
        <span
          className={`font-mono text-xs font-bold ${
            item.net > 0
              ? 'text-brand-600 dark:text-brand-400'
              : item.net < 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-content-muted'
          }`}
        >
          {item.net > 0 ? `+${item.net}` : item.net}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Tendência',
      render: (item) => (
        <span className="text-2xs text-content-muted">
          {item.net > 0 ? 'Crescimento' : item.net < 0 ? 'Evasão' : 'Estável'}
        </span>
      ),
    },
  ];

  const campaignColumns: Column<CampaignAdSpend>[] = [
    {
      key: 'campaign_name',
      header: 'Campanha',
      render: (item) => (
        <div>
          <span className="font-semibold text-content-main">
            {item.campaign_name}
          </span>
          {item.notes && (
            <p className="text-2xs text-content-muted">{item.notes}</p>
          )}
        </div>
      ),
    },
    {
      key: 'group',
      header: 'Grupo',
      render: (item) => {
        const grp = groups.find((g) => g.id === item.group_id);
        return (
          <span className="text-xs text-content-muted">
            {grp ? grp.name : 'Todos os Grupos'}
          </span>
        );
      },
    },
    {
      key: 'amount',
      header: 'Investimento (R$)',
      render: (item) => (
        <span className="font-mono text-xs font-bold text-content-main">
          R$ {item.amount.toFixed(2).replace('.', ',')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (item) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDeleteAdSpend(item.id)}
          isLoading={deletingSpendId === item.id}
          aria-label="Excluir gasto"
          className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Monitor de Evasões & CPL Real"
        subtitle="Telemetria de retenção horária, taxa de churn e custo real por lead em grupos VIP"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {onNavigateToGroups && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onNavigateToGroups}
              >
                <Layers className="h-3.5 w-3.5" />
                <span>Lista de Grupos</span>
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={loadTelemetry}
              isLoading={isLoading}
              aria-label="Atualizar telemetria"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span>Atualizar</span>
            </Button>
            <Button variant="primary" size="sm" onClick={handleOpenDrawer}>
              <Plus className="h-3.5 w-3.5" />
              <span>Registrar Gasto</span>
            </Button>
          </div>
        }
      />

      {/* Controles de Filtro e Inserção Rápida de Tráfego Diário */}
      <Card>
        <Card.Body className="p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Input
                label="Data de Análise"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                leftIcon={<Calendar className="h-4 w-4" />}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-content-main">
                Grupo VIP Filtrado
              </label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="focus:outline-hidden w-full rounded-lg border border-surface-border bg-surface-panel px-3 py-2 text-xs text-content-main focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="">Todos os Grupos VIP</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.current_members} membros)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Input
                label="Investimento Rápido em Tráfego (Hoje - R$)"
                placeholder="ex: 150.00"
                value={manualAdSpendInput}
                onChange={(e) => handleManualSpendChange(e.target.value)}
                leftIcon={<DollarSign className="h-4 w-4" />}
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Widgets de KPI */}
      {isLoading && !telemetry ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <LoadingSkeleton height="100px" />
          <LoadingSkeleton height="100px" />
          <LoadingSkeleton height="100px" />
          <LoadingSkeleton height="100px" />
          <LoadingSkeleton height="100px" />
          <LoadingSkeleton height="100px" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricWidget
            label="Total de Entradas"
            value={`+${summary.total_joined}`}
            icon={UserPlus}
            trend={{
              value: `${summary.total_joined} novos leads`,
              positive: summary.total_joined > 0,
            }}
            badge={<Badge variant="online">Entradas</Badge>}
          />

          <MetricWidget
            label="Evasões (Saídas de Membros)"
            value={`-${summary.total_left}`}
            icon={UserMinus}
            trend={{
              value: `${summary.total_left} saídas`,
              positive: summary.total_left === 0 ? null : false,
            }}
            badge={
              <Badge variant={summary.total_left > 0 ? 'warning' : 'neutral'}>
                Churn
              </Badge>
            }
          />

          <MetricWidget
            label="Saldo Líquido (Entradas - Saídas)"
            value={
              summary.net_growth > 0
                ? `+${summary.net_growth}`
                : `${summary.net_growth}`
            }
            icon={Users}
            trend={{
              value:
                summary.net_growth > 0
                  ? 'Retenção Positiva'
                  : summary.net_growth < 0
                    ? 'Retenção Negativa'
                    : 'Neutro',
              positive: summary.net_growth > 0,
            }}
            badge={
              <Badge variant={summary.net_growth > 0 ? 'online' : 'neutral'}>
                Líquido
              </Badge>
            }
          />

          <MetricWidget
            label="Taxa de Evasão (Churn Rate)"
            value={`${summary.churn_rate}%`}
            icon={AlertTriangle}
            description="Proporção de saídas em relação às entradas"
            badge={
              <Badge variant={summary.churn_rate > 20 ? 'warning' : 'online'}>
                {summary.churn_rate <= 10
                  ? 'Excelente'
                  : summary.churn_rate <= 25
                    ? 'Aceitável'
                    : 'Atenção'}
              </Badge>
            }
          />

          <MetricWidget
            label="Investimento Total em Anúncios"
            value={`R$ ${summary.total_ad_spend.toFixed(2).replace('.', ',')}`}
            icon={DollarSign}
            description={
              manualAdSpendInput.trim()
                ? 'Valor manual de simulação ativo'
                : `${campaigns.length} campanha(s) registrada(s)`
            }
            badge={<Badge variant="neutral">Tráfego</Badge>}
          />

          <MetricWidget
            label="CPL Real (Custo por Lead Líquido)"
            value={
              summary.real_cpl !== null && summary.real_cpl > 0
                ? `R$ ${summary.real_cpl.toFixed(2).replace('.', ',')}`
                : summary.net_growth <= 0
                  ? 'Sem saldo líq.'
                  : 'R$ 0,00'
            }
            icon={Zap}
            description="CPL Real = Gasto / (Entradas - Saídas)"
            badge={
              <Badge
                variant={
                  summary.net_growth > 0 &&
                  summary.real_cpl !== null &&
                  summary.real_cpl < 5
                    ? 'online'
                    : 'warning'
                }
              >
                {summary.net_growth <= 0 ? 'Saldo Negativo' : 'CPL Efetivo'}
              </Badge>
            }
          />
        </div>
      )}

      {/* Gráfico de Entradas x Saídas por Hora */}
      <Card>
        <Card.Header
          title="Fluxo Horário de Retenção"
          subtitle="Distribuição de adições e evasões registradas hora a hora nas últimas 24 horas"
        />
        <Card.Body className="p-4 sm:p-6">
          <div className="space-y-4">
            {/* Legenda */}
            <div className="flex items-center justify-end gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="rounded-xs h-3 w-3 bg-brand-500" />
                <span className="font-medium text-content-main">Entradas</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="rounded-xs h-3 w-3 bg-rose-500" />
                <span className="font-medium text-content-main">Saídas</span>
              </div>
            </div>

            {/* Linha do Tempo Visual Horária */}
            <div className="flex items-end gap-1 overflow-x-auto pb-2 pt-6">
              {hourlyData.map((h) => {
                const joinedHeightPercent = Math.round(
                  (h.joined / maxActivity) * 100
                );
                const leftHeightPercent = Math.round(
                  (h.left / maxActivity) * 100
                );

                return (
                  <div
                    key={h.hour}
                    className="group relative flex min-w-7 flex-1 flex-col items-center"
                  >
                    {/* Tooltip no hover */}
                    <div className="pointer-events-none absolute -top-12 z-20 hidden flex-col items-center rounded-md border border-surface-border bg-surface-panel px-2 py-1 shadow-md group-hover:flex">
                      <span className="text-2xs font-bold text-content-main">
                        {h.hour_label}
                      </span>
                      <span className="text-micro text-content-muted">
                        +{h.joined} | -{h.left} (Saldo: {h.net})
                      </span>
                    </div>

                    {/* Barras Lado a Lado */}
                    <div className="flex h-32 w-full items-end justify-center gap-0.5 rounded-md bg-surface-subtle p-0.5">
                      {/* Barra de Entradas */}
                      <div
                        style={{
                          height: `${Math.max(joinedHeightPercent, h.joined > 0 ? 8 : 0)}%`,
                        }}
                        className={`rounded-t-xs w-1/2 transition-all ${
                          h.joined > 0 ? 'bg-brand-500' : 'bg-transparent'
                        }`}
                        title={`Entradas: +${h.joined}`}
                      />

                      {/* Barra de Saídas */}
                      <div
                        style={{
                          height: `${Math.max(leftHeightPercent, h.left > 0 ? 8 : 0)}%`,
                        }}
                        className={`rounded-t-xs w-1/2 transition-all ${
                          h.left > 0 ? 'bg-rose-500' : 'bg-transparent'
                        }`}
                        title={`Saídas: -${h.left}`}
                      />
                    </div>

                    {/* Rótulo da Hora */}
                    <span className="mt-1 text-2xs text-content-muted">
                      {h.hour % 3 === 0 ? `${h.hour}h` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Tabela de Detalhamento Horário com Atividade */}
      {activeHours.length > 0 && (
        <Card>
          <Card.Header
            title="Horários com Movimentação Registrada"
            subtitle={`${activeHours.length} horário(s) com atividade registrada na data`}
          />
          <Card.Body className="p-0">
            <DataTable
              data={activeHours}
              columns={hourlyColumns}
              keyExtractor={(item) => item.hour_label}
              emptyMessage="Nenhuma movimentação neste dia."
            />
          </Card.Body>
        </Card>
      )}

      {/* Tabela de Gastos por Campanha */}
      <Card>
        <Card.Header
          title="Campanhas de Tráfego do Dia"
          subtitle="Investimento registrado para o cálculo fidedigno do CPL Real"
          actions={
            <Button variant="secondary" size="sm" onClick={handleOpenDrawer}>
              <Plus className="h-3.5 w-3.5" />
              <span>Nova Campanha</span>
            </Button>
          }
        />
        <Card.Body className="p-0">
          <DataTable
            data={campaigns}
            columns={campaignColumns}
            keyExtractor={(item) => item.id}
            emptyMessage="Nenhum investimento registrado para esta data. Clique em 'Registrar Gasto' para adicionar."
          />
        </Card.Body>
      </Card>

      {/* Drawer para Inserção de Gasto em Anúncios */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Registrar Investimento em Tráfego"
        description="Insira os gastos com anúncios para cálculo preciso do Custo por Lead Real"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsDrawerOpen(false)}
              disabled={isSavingAdSpend}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveAdSpend}
              isLoading={isSavingAdSpend}
            >
              Salvar Investimento
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome da Campanha"
            placeholder="ex: Meta Ads - Escala VIP 01"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            error={formErrors.campaignName}
          />

          <Input
            label="Valor Investido (R$)"
            type="number"
            step="0.01"
            placeholder="ex: 150.00"
            value={campaignAmount}
            onChange={(e) => setCampaignAmount(e.target.value)}
            error={formErrors.campaignAmount}
            leftIcon={<DollarSign className="h-4 w-4" />}
          />

          <Input
            label="Data do Gasto"
            type="date"
            value={campaignDate}
            onChange={(e) => setCampaignDate(e.target.value)}
          />

          <div>
            <label className="mb-1.5 block text-xs font-medium text-content-main">
              Grupo VIP Vinculado
            </label>
            <select
              value={campaignGroupId}
              onChange={(e) => setCampaignGroupId(e.target.value)}
              className="focus:outline-hidden w-full rounded-lg border border-surface-border bg-surface-panel px-3 py-2 text-xs text-content-main focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Geral (Todos os Grupos VIP)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-2xs text-content-muted">
              Selecione um grupo específico ou deixe geral para rateio no canal.
            </p>
          </div>

          <Input
            label="Observações / Anotações"
            placeholder="ex: Campanha de conversão direta para grupo VIP"
            value={campaignNotes}
            onChange={(e) => setCampaignNotes(e.target.value)}
          />
        </div>
      </Drawer>
    </div>
  );
};
